import * as Sentry from '@sentry/react';
import { AiCoach_CourseraProductType as CourseraProductType } from '__generated__/graphql-types';

import { isAuthenticatedUser } from 'js/lib/user';

import localStorageEx from 'bundles/common/utils/localStorageEx';
import sessionStorageEx from 'bundles/common/utils/sessionStorageEx';
import { RECENTLY_VIEWED_LOCAL_STORAGE_NAME } from 'bundles/search-common/constants';
import type { RecentlyViewed, Viewed } from 'bundles/search-common/types/autocomplete';
import inServerContext from 'bundles/ssr/util/inServerContext';

export const LOHP_VIEW_TIMESTAMP_KEY = 'lohp-view-timestamp';
export const SHOW_AI_ASSISTANT_TIMESTAMP_KEY = 'show-ai-assistant-timestamp';
export const VIEWED_XDPS_FOR_COACH_KEY = 'viewed-xdps-for-coach';
export const ACTIVE_EXPERIMENT_STORAGE_KEY = 'lohp_ai_assistance_active_experiment';

export const EXPERIMENT_TYPE = {
  PRODUCT_DECISION: 'productDecision',
  SIGNUP_ABANDONMENT: 'signupAbandonment',
};

export type ExperimentType = (typeof EXPERIMENT_TYPE)[keyof typeof EXPERIMENT_TYPE];

const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;

/**
 * Maps product type strings to GraphQL CourseraProductType enum values
 * Handles special cases like 'Professional Certificate' -> 'Specialization'
 */
export const mapToGraphQLProductType = (productType: string): CourseraProductType | null => {
  // Special case: Professional Certificate maps to Specialization
  if (productType === 'Professional Certificate') {
    return CourseraProductType.Specialization;
  }

  if (productType === 'Guided Project') {
    return CourseraProductType.Course;
  }

  // Check if it's a valid enum key
  if (productType in CourseraProductType) {
    return CourseraProductType[productType as keyof typeof CourseraProductType];
  }

  return null;
};

export const isUserLoggedOut = (): boolean => {
  return !isAuthenticatedUser();
};

/**
 * Sets the LOHP view timestamp in localStorage to track initial landing on LOHP
 */
export const setLOHPViewTimestamp = (): void => {
  if (inServerContext || !localStorageEx.isAvailable()) {
    return;
  }

  const timestamp = Date.now();
  localStorageEx.setItem(LOHP_VIEW_TIMESTAMP_KEY, timestamp, String);
};

/**
 * Gets the LOHP view timestamp from localStorage
 * @returns timestamp number or null if not found
 */
export const getLOHPViewTimestamp = (): number | null => {
  if (inServerContext || !localStorageEx.isAvailable()) {
    return null;
  }

  const timestamp = localStorageEx.getItem(LOHP_VIEW_TIMESTAMP_KEY, String, null);
  if (!timestamp) {
    return null;
  }

  const parsed = parseInt(timestamp, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const clearLOHPViewTimestamp = (): void => {
  if (inServerContext || !localStorageEx.isAvailable()) {
    return;
  }

  localStorageEx.removeItem(LOHP_VIEW_TIMESTAMP_KEY);
};

/**
 * Checks if the LOHP view timestamp exists and is within the last 30 days
 */
export const hasValidLOHPViewTimestamp = (): boolean => {
  const timestamp = getLOHPViewTimestamp();
  if (!timestamp) {
    return false;
  }

  const now = Date.now();
  return now - timestamp <= THIRTY_DAYS_IN_MS;
};

/**
 * Gets recently viewed items from localStorage
 */
export const getRecentlyViewedItems = (): RecentlyViewed[] => {
  if (inServerContext || !localStorageEx.isAvailable()) {
    return [];
  }

  return localStorageEx.getItem(RECENTLY_VIEWED_LOCAL_STORAGE_NAME, JSON.parse, []);
};

/**
 * Sets the timestamp when AI assistant was first shown in sessionStorage
 * This should be set to the original LOHP view timestamp to track XDPs viewed after initial LOHP visit
 * @param timestamp - The original LOHP view timestamp
 */
export const setShowAiAssistantTimestamp = (timestamp: number): void => {
  if (inServerContext || !sessionStorageEx.isAvailable()) {
    return;
  }

  sessionStorageEx.setItem(SHOW_AI_ASSISTANT_TIMESTAMP_KEY, timestamp, String);
};

/**
 * Gets the timestamp when AI assistant was first shown from sessionStorage
 * @returns timestamp number or null if not found
 */
export const getShowAiAssistantTimestamp = (): number | null => {
  if (inServerContext || typeof sessionStorage === 'undefined') {
    return null;
  }

  const timestamp = sessionStorageEx.getItem(SHOW_AI_ASSISTANT_TIMESTAMP_KEY, String, null);
  if (!timestamp) {
    return null;
  }
  const parsed = parseInt(timestamp, 10);
  return Number.isNaN(parsed) ? null : parsed;
};

export const getIdFromProductId = (productId: string) => {
  return productId.split(/~(.+)/)[1]?.replace('!', '');
};

export const getViewedXDPsAfterTimestamp = (afterTimestamp: number): Viewed[] => {
  const recentlyViewed = getRecentlyViewedItems();

  return recentlyViewed
    .filter((item) => {
      const {
        dateSaved,
        suggestion: { productType, id },
      } = item;

      if (!productType || !id) {
        return false;
      }

      if (!mapToGraphQLProductType(productType)) {
        return false;
      }

      if (!getIdFromProductId(id)) {
        return false;
      }

      const itemTimestamp = new Date(dateSaved).getTime();

      if (Number.isNaN(itemTimestamp)) {
        Sentry.captureMessage('Invalid dateSaved in recently viewed item', {
          extra: { dateSaved: item.dateSaved, item },
          level: 'warning',
        });
        return false;
      }

      return itemTimestamp > afterTimestamp;
    })
    .slice(0, 5)
    .map((item) => item.suggestion);
};

/**
 * Clears the showAiAssistantTimestamp from sessionStorage
 */
export const clearShowAiAssistantTimestamp = (): void => {
  if (inServerContext || !sessionStorageEx.isAvailable()) {
    return;
  }

  sessionStorageEx.removeItem(SHOW_AI_ASSISTANT_TIMESTAMP_KEY);
};

/**
 * Checks if the first signal check passes:
 * - LOHP view timestamp exists (within last 30 days)
 * - Recently viewed includes at least one XDP after that timestamp
 * - User is logged out
 */
export const checkFirstSignal = (): boolean => {
  if (!isUserLoggedOut()) {
    return false;
  }

  if (!hasValidLOHPViewTimestamp()) {
    return false;
  }

  const timestamp = getLOHPViewTimestamp();
  if (!timestamp) {
    return false;
  }

  return getViewedXDPsAfterTimestamp(timestamp).length > 0;
};

/**
 * Checks if the session check passes:
 * - show-ai-assistant-timestamp exists in sessionStorage
 * - User is logged out
 */
export const checkSessionSignal = (): boolean => {
  if (!isUserLoggedOut()) {
    return false;
  }

  return getShowAiAssistantTimestamp() !== null;
};

/**
 * Gets the active experiment from sessionStorage
 * Used to persist which AI assistance experiment was shown first in the session
 * @returns ExperimentType or null if not found
 */
export const getActiveExperiment = (): ExperimentType | null => {
  if (inServerContext || typeof sessionStorage === 'undefined') {
    return null;
  }

  try {
    const experiment = sessionStorage.getItem(ACTIVE_EXPERIMENT_STORAGE_KEY);
    return experiment as ExperimentType | null;
  } catch (err) {
    Sentry.captureException(err, {
      extra: { message: 'Failed to get active experiment from sessionStorage' },
    });
    return null;
  }
};

/**
 * Sets the active experiment in sessionStorage
 * This persists which experiment was shown first across page refreshes within the session
 * @param experiment - The experiment type to set
 */
export const setActiveExperiment = (experiment: ExperimentType): void => {
  if (inServerContext || typeof sessionStorage === 'undefined') {
    return;
  }

  try {
    sessionStorage.setItem(ACTIVE_EXPERIMENT_STORAGE_KEY, experiment);
  } catch (err) {
    Sentry.captureException(err, {
      extra: { message: 'Failed to set active experiment in sessionStorage' },
    });
  }
};

/**
 * Clears the active experiment from sessionStorage
 */
export const clearActiveExperiment = (): void => {
  if (inServerContext || !sessionStorageEx.isAvailable()) {
    return;
  }

  sessionStorageEx.removeItem(ACTIVE_EXPERIMENT_STORAGE_KEY);
};
